import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import GoalInput from './components/GoalInput';
import Results from './components/Results';
import About from './components/About';
import Walkthrough from './components/Walkthrough';
import './App.css';

function AppContent({ onOpenGuide }) {
  const navigate = useNavigate();

  const [goals, setGoals] = useState(() => {
    try {
      const saved = localStorage.getItem('investt_goals');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Error parsing goals from localStorage", e);
      return [];
    }
  });

  const [userProfile, setUserProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('investt_profile');
      return saved ? JSON.parse(saved) : {
        age: '',
        monthlyIncome: '',
        monthlyExpenses: '',
        existingInvestments: '',
        riskComfort: 'Medium'
      };
    } catch (e) {
      console.error("Error parsing profile from localStorage", e);
      return {
        age: '',
        monthlyIncome: '',
        monthlyExpenses: '',
        existingInvestments: '',
        riskComfort: 'Medium'
      };
    }
  });

  const [currentStep, setCurrentStep] = useState(() => {
    return goals.length > 0 ? 'results' : 'input';
  });

  useEffect(() => {
    try {
      localStorage.setItem('investt_goals', JSON.stringify(goals));
    } catch (e) {
      console.error("Error saving goals", e);
    }
  }, [goals]);

  useEffect(() => {
    try {
      localStorage.setItem('investt_profile', JSON.stringify(userProfile));
    } catch (e) {
      console.error("Error saving profile", e);
    }
  }, [userProfile]);

  const addGoal = (goal) => {
    setGoals([...goals, { ...goal, id: Date.now() }]);
  };

  const removeGoal = (goalId) => {
    setGoals(goals.filter(goal => goal.id !== goalId));
  };

  const updateGoal = (goalId, updatedGoal) => {
    setGoals(goals.map(goal => goal.id === goalId ? { ...updatedGoal, id: goalId } : goal));
  };

  const updateUserProfile = (profile) => {
    setUserProfile(profile);
  };

  const proceedToResults = () => {
    if (goals.length > 0) {
      setCurrentStep('results');
      navigate('/results');
    }
  };

  const resetSimulation = () => {
    if (window.confirm("Are you sure you want to clear all data and start over?")) {
      setGoals([]);
      setUserProfile({
        age: '',
        monthlyIncome: '',
        monthlyExpenses: '',
        existingInvestments: '',
        riskComfort: 'Medium'
      });
      setCurrentStep('input');
      localStorage.removeItem('investt_goals');
      localStorage.removeItem('investt_profile');
      navigate('/');
    }
  };

  const backToInput = () => {
    setCurrentStep('input');
    navigate('/');
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Hero />
            <GoalInput
              goals={goals}
              addGoal={addGoal}
              removeGoal={removeGoal}
              updateGoal={updateGoal}
              userProfile={userProfile}
              updateUserProfile={updateUserProfile}
              proceedToResults={proceedToResults}
            />
          </motion.div>
        } />
        <Route path="/results" element={
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Results
              goals={goals}
              userProfile={userProfile}
              resetSimulation={resetSimulation}
              backToInput={backToInput}
            />
          </motion.div>
        } />
        <Route path="/about" element={<About />} />
      </Routes>
    </>
  );
}

function App() {
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // Auto-show walkthrough on first visit
  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('investt_guide_seen');
    if (!hasSeenGuide) {
      // Show guide automatically on first visit
      setTimeout(() => {
        setShowWalkthrough(true);
        localStorage.setItem('investt_guide_seen', 'true');
      }, 1000);
    }
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar onOpenGuide={() => setShowWalkthrough(true)} />
        <AppContent onOpenGuide={() => setShowWalkthrough(true)} />
        <Walkthrough isOpen={showWalkthrough} onClose={() => setShowWalkthrough(false)} />
      </div>
    </Router>
  );
}

export default App;